/**
 * Public Trust Centre content — scoped to match peer EMS platforms
 * (Cvent Trust Center / Gevme Trust Center): compliance, product & data
 * security, privacy, infrastructure, legal, AI governance, and report access.
 *
 * Do not claim completed attestations that are not yet issued.
 */

export const TRUST_LAST_UPDATED = "September 22, 2026";

export const TRUST_HERO = {
  overline: "Trust Centre",
  title: "Security, privacy & compliance for event data",
  lede:
    "Aivory EMS protects organiser and attendee data with the same control families enterprise buyers review at Cvent and Gevme — encryption, access control, auditability, privacy, availability, and independent assurance. We are building toward SOC 2 Type II on this scope.",
};

/** Compliance posture shown as badges (status is explicit). */
export const COMPLIANCE_ITEMS = [
  {
    id: "soc2",
    name: "SOC 2 Type II",
    status: "In progress",
    detail:
      "Controls mapped to the AICPA Trust Services Criteria (Security, Availability, Confidentiality, Processing Integrity, Privacy). Type II observation period and independent attestation are in flight.",
  },
  {
    id: "pdpa",
    name: "Singapore PDPA",
    status: "Aligned",
    detail:
      "Personal data handled under Singapore PDPA obligations — purpose limitation, access/correction, retention, and protection — as described in our Privacy Policy.",
  },
  {
    id: "gdpr",
    name: "GDPR / UK GDPR",
    status: "Aligned",
    detail:
      "Lawful basis, data subject rights, subprocessors, and cross-border transfer language for EU/UK attendees and organisers where applicable.",
  },
  {
    id: "ccpa",
    name: "CCPA / CPRA",
    status: "Aligned",
    detail:
      "We do not sell personal information. Consumer rights requests for covered residents are handled via our privacy contact.",
  },
  {
    id: "pci",
    name: "Payment card data",
    status: "Processor model",
    detail:
      "Card data is processed by certified payment providers (e.g. Revolut Merchant / Stripe). Aivory EMS does not store full PAN or CVV on the application database.",
  },
  {
    id: "ai",
    name: "AI governance",
    status: "Controls live",
    detail:
      "AivoryOne is tenant-scoped, human-confirmed for high-impact actions, and not used to train shared public models on your content without written consent.",
  },
];

export const PRODUCT_SCOPE = {
  title: "In scope products",
  body: "The Trust Centre covers the Aivory EMS cloud platform used to plan and run events:",
  products: [
    { name: "Aivory EMS", items: ["Registration & ticketing", "Event websites & forms", "Email campaigns", "QR check-in & badges", "Wallet passes", "Analytics & reporting"] },
    { name: "AivoryOne", items: ["AI co-planner", "Setup workflows", "Tenant-isolated prompts & audit"] },
    { name: "Operator platform", items: ["Multi-tenant workspaces", "Team roles & SSO (enterprise)", "Platform billing & ops console"] },
  ],
};

export const CONTROL_SECTIONS = [
  {
    id: "product-security",
    title: "Product security",
    items: [
      {
        title: "Role-based access control",
        body: "Workspace roles and permissions gate admin features. Platform operators are separated from tenant workspaces.",
      },
      {
        title: "Audit logging",
        body: "Sensitive operator actions and AI mutations are recorded for accountability and incident review.",
      },
      {
        title: "SSO (enterprise)",
        body: "Optional OIDC / SAML-style enterprise sign-in for workspaces that require federated identity.",
      },
      {
        title: "Service levels",
        body: "Enterprise plans include dedicated support SLAs. Live component health is published at /status.",
      },
    ],
  },
  {
    id: "data-security",
    title: "Data security",
    items: [
      {
        title: "Encryption in transit",
        body: "HTTPS / TLS for all public endpoints. Non-HTTPS requests are upgraded to TLS.",
      },
      {
        title: "Encryption at rest",
        body: "Disk-level encryption on production volumes; secrets and passwords stored hashed / vaulted, never in plaintext.",
      },
      {
        title: "Backups",
        body: "Scheduled application database backups with restore procedures for disaster recovery.",
      },
      {
        title: "Data erasure",
        body: "Tenant offboarding and deletion workflows support contractual and PDPA/GDPR erasure requests.",
      },
      {
        title: "Tenant isolation",
        body: "Attendee and event data is scoped per workspace; API and UI paths enforce tenant boundaries.",
      },
    ],
  },
  {
    id: "app-security",
    title: "Application security",
    items: [
      {
        title: "Secure SDLC",
        body: "Code review, dependency hygiene, and regression tests for auth, webhooks, and tenant isolation.",
      },
      {
        title: "Credential management",
        body: "JWT signing secrets enforced at boot in production; password resets use time-limited tokens emailed to operators.",
      },
      {
        title: "Webhook authenticity",
        body: "Inbound provider webhooks verified with shared signing secrets where supported (e.g. Revolut, email providers).",
      },
      {
        title: "Responsible disclosure",
        body: "Report vulnerabilities to security@aivoryems.com. Policy published at /.well-known/security.txt.",
      },
    ],
  },
  {
    id: "access-control",
    title: "Access control",
    items: [
      {
        title: "Need-to-know access",
        body: "Production access limited to authorised operators; access reviewed on role change and offboarding.",
      },
      {
        title: "Password security",
        body: "Operator passwords hashed with modern algorithms; session tokens are signed and expiry-bound.",
      },
      {
        title: "Logging & monitoring",
        body: "Health checks, capability probes, and operational alerts monitor API and AI subsystem availability.",
      },
    ],
  },
  {
    id: "infrastructure",
    title: "Infrastructure",
    items: [
      {
        title: "Primary hosting",
        body: "Production application stack hosted in Singapore (DigitalOcean) with TLS termination via Caddy.",
      },
      {
        title: "Regional residency",
        body: "Enterprise options for Singapore, Malaysia, and Australia residency where contracted.",
      },
      {
        title: "Containerised services",
        body: "Backend, frontend, reverse proxy, and optional AI sidecars (vector store / cache) run as isolated containers.",
      },
      {
        title: "BC / DR",
        body: "Backup restore and rebuild procedures for the application stack; status and maintenance notices on /status.",
      },
    ],
  },
  {
    id: "network-security",
    title: "Network security",
    items: [
      {
        title: "TLS everywhere",
        body: "Public sites and APIs terminate TLS; HSTS and secure cookie practices on operator sessions.",
      },
      {
        title: "Edge protection",
        body: "Reverse proxy terminates TLS and routes only intended services; admin APIs require authentication.",
      },
      {
        title: "Least exposure",
        body: "Datastores and internal AI services are not exposed publicly; only the reverse proxy faces the internet.",
      },
    ],
  },
  {
    id: "corporate-security",
    title: "Corporate security",
    items: [
      {
        title: "Employee & contractor controls",
        body: "NDAs and access agreements for staff handling customer data; least-privilege production access.",
      },
      {
        title: "Security awareness",
        body: "Ongoing awareness of phishing, credential hygiene, and data handling for customer PII.",
      },
      {
        title: "Vendor management",
        body: "Subprocessors reviewed for security and privacy posture; listed in the Privacy Policy.",
      },
    ],
  },
  {
    id: "data-privacy",
    title: "Data privacy",
    items: [
      {
        title: "Privacy programme",
        body: "Published Privacy Policy covering PDPA, GDPR, AI processing, retention, and rights requests.",
      },
      {
        title: "Data Protection Officer contact",
        body: "Privacy enquiries: privacy@aivoryems.com (or the contact named in the Privacy Policy).",
      },
      {
        title: "Breach notification",
        body: "Affected customers notified without undue delay after confirming a personal data breach, consistent with PDPA/GDPR timelines and contract terms.",
      },
      {
        title: "We do not sell data",
        body: "Organiser and attendee data exists to run your events — not for sale or unrelated advertising.",
      },
    ],
  },
  {
    id: "legal",
    title: "Legal & customer rights",
    items: [
      {
        title: "Subprocessors",
        body: "Infrastructure, email, payments, and AI inference providers are disclosed in the Privacy Policy.",
      },
      {
        title: "Customer audit rights",
        body: "Enterprise customers may request security questionnaires, architecture summaries, and (when issued) SOC 2 reports under NDA.",
      },
      {
        title: "Terms & DPA",
        body: "Terms of Service govern platform use; data processing terms available for enterprise and government programmes.",
      },
    ],
  },
  {
    id: "ai-governance",
    title: "AI governance (AivoryOne)",
    items: [
      {
        title: "Your data, your workspace",
        body: "Prompts and tools run in the tenant context. We do not train shared public models on your content without written consent.",
      },
      {
        title: "Governed execution loop",
        body: "AivoryONE is designed to Understand → Analyse → Recommend → Execute → Audit — preferring in-product tools over free-text advice alone.",
      },
      {
        title: "Human confirmation",
        body: "Publish, import, and bulk actions require human confirmation before they take effect.",
      },
      {
        title: "Safety controls",
        body: "Secret redaction before model calls, prompt-injection refusal, output filtering, and LLM audit records (prompt hash, actor, model label).",
      },
      {
        title: "Feature control",
        body: "AI features can be limited by plan and workspace settings; mock mode available for staging.",
      },
    ],
  },
];

export const REPORTS = [
  {
    id: "soc2-report",
    title: "SOC 2 Type II report",
    body: "Available to customers under NDA when the Type II attestation is issued. Request early access to the control matrix and roadmap meanwhile.",
  },
  {
    id: "pentest",
    title: "Penetration test summary",
    body: "Independent or third-party penetration test executive summaries shared with qualified prospects and customers on request.",
  },
  {
    id: "sig-caiq",
    title: "SIG / CAIQ / security questionnaire",
    body: "Standardised Information Gathering (SIG), CAIQ, or your vendor security questionnaire — completed for enterprise evaluations.",
  },
  {
    id: "architecture",
    title: "Architecture & data-flow overview",
    body: "High-level system and data-flow diagrams for security reviews (no sensitive internals).",
  },
];

export const FAQS = [
  {
    q: "Are you SOC 2 certified today?",
    a: "We are executing a SOC 2 Type II programme against the Trust Services Criteria used by peer event platforms. Controls are in place and being evidenced; the independent Type II report will be shared under NDA once issued. Request access below to track status.",
  },
  {
    q: "Do you encrypt data in transit and at rest?",
    a: "Yes. Public traffic uses TLS. Production volumes use disk encryption; passwords are hashed; payment card data stays with certified payment processors.",
  },
  {
    q: "Where is customer data hosted?",
    a: "Primary production is in Singapore. Enterprise contracts may specify Malaysia or Australia residency options.",
  },
  {
    q: "Will you notify us of a security incident?",
    a: "Yes. Impacted customers are notified after confirmation of a relevant incident or personal data breach, per contract and applicable law.",
  },
  {
    q: "Do you perform penetration testing?",
    a: "Yes — internal security testing plus third-party assessments on a planned cadence. Executive summaries are available to customers on request.",
  },
  {
    q: "Do you support SSO?",
    a: "Enterprise workspaces can enable OIDC/SAML-style SSO. Contact sales for setup on your identity provider.",
  },
  {
    q: "Is payment card data stored in Aivory EMS?",
    a: "No. Card payments are handled by Revolut Merchant, Stripe, or other certified processors. We do not store full card numbers or CVV.",
  },
  {
    q: "How is AivoryOne AI governed?",
    a: "Tenant isolation, human confirmation for high-impact actions, secret redaction, audit records, and no training of shared public models on your content without written consent.",
  },
  {
    q: "How do I report a vulnerability?",
    a: "Email security@aivoryems.com. See /.well-known/security.txt for preferred languages and the canonical policy URL.",
  },
];

export const TOC = [
  { id: "compliance", label: "Compliance" },
  { id: "scope", label: "Scope" },
  { id: "product-security", label: "Product" },
  { id: "data-security", label: "Data" },
  { id: "app-security", label: "Application" },
  { id: "access-control", label: "Access" },
  { id: "infrastructure", label: "Infrastructure" },
  { id: "network-security", label: "Network" },
  { id: "corporate-security", label: "Corporate" },
  { id: "data-privacy", label: "Privacy" },
  { id: "legal", label: "Legal" },
  { id: "ai-governance", label: "AI" },
  { id: "reports", label: "Reports" },
  { id: "faq", label: "FAQ" },
  { id: "request", label: "Request access" },
];
